import { useState, useEffect, useMemo, useCallback } from 'react';
import { useInquiryFilters } from './parts/history/useInquiryFilters';
import { useInquiryPagination } from './parts/history/useInquiryPagination';
import { sortByDate, filterInquiryData, formatNow } from '../utils/inquiryUtils';

const ITEMS_PER_PAGE = 5;

/**
 * 질의 내역 페이지의 상태 관리 및 비즈니스 로직을 총괄하는 메인 훅.
 * 필터(useInquiryFilters)와 페이징(useInquiryPagination)을 조합합니다.
 */
export const useInquiryHistory = (viewMode, currentUser, realHistoryList = [], isLoadingHistory = false) => {
    // 1. 전체 데이터 및 상세 보기 상태
    const [historyData, setHistoryData] = useState([]);
    const [selectedInquiry, setSelectedInquiry] = useState(null);

    // 실제 데이터(서버 응답)가 들어오면 동기화
    useEffect(() => {
        setHistoryData(realHistoryList);
    }, [realHistoryList]);

    // 2. 필터 상태 관리 (useInquiryFilters)
    const {
        personalSearch, setPersonalSearch,
        companyNameSearch, setCompanyNameSearch,
        companyContentSearch, setCompanyContentSearch,
        adminCompanySearch, setAdminCompanySearch,
        statusFilter, setStatusFilter,
        sortOrder, setSortOrder,
        startDate, setStartDate,
        endDate, setEndDate,
        clearDateFilter,
        filters
    } = useInquiryFilters();

    // 3. 필터링 및 정렬된 결과 계산 수동 메모이제이션
    const filteredHistory = useMemo(() => {
        const filtered = filterInquiryData(historyData, filters, viewMode, currentUser);
        return sortByDate(filtered, sortOrder);
    }, [historyData, filters, viewMode, currentUser, sortOrder]);

    // 4. 페이징 처리 (useInquiryPagination)
    // 필터나 정렬값이 바뀌면 useInquiryPagination 내부에서 1페이지로 자동 리셋됨
    const {
        currentPage,
        setCurrentPage,
        totalPages,
        paginatedData
    } = useInquiryPagination(filteredHistory, ITEMS_PER_PAGE, [
        viewMode, personalSearch, companyNameSearch, companyContentSearch, 
        adminCompanySearch, startDate, endDate, statusFilter, sortOrder
    ]);

    // 5. 사이드바 최근 질의 내역 (필터링되지 않은 원본 데이터 기준 상단 5개)
    const recentInquiries = useMemo(() => {
        const list = [...historyData].filter(item => {
            if (viewMode !== 'personal') return true;
            if (!item.user_id || !currentUser.id) return true;
            return String(item.user_id) === String(currentUser.id);
        });
        return sortByDate(list, 'desc').slice(0, 5);
    }, [historyData, viewMode, currentUser.id]);

    // 6. 데이터 조작 핸들러 (낙관적 UI용)
    const addHistory = useCallback((question, answer, status = "success", customId = null) => {
        const newEntry = {
            id: customId || Date.now(),
            user_id: currentUser.id,
            user_name: currentUser.name,
            company_code: currentUser.company,
            question,
            answer,
            created_at: formatNow(),
            answer_status: status
        };
        setHistoryData(prev => [newEntry, ...prev]);
    }, [currentUser]);

    const removeHistoryItem = useCallback((id) => {
        setHistoryData(prev => prev.filter(item => item.id !== id));
        if (selectedInquiry?.id === id) setSelectedInquiry(null);
    }, [selectedInquiry?.id]);

    const handleDelete = useCallback((id) => {
        if (window.confirm('정말로 이 질의 기록을 삭제하시겠습니까?')) {
            removeHistoryItem(id);
        }
    }, [removeHistoryItem]);

    return {
        // 결과 데이터
        paginatedData,
        recentInquiries,
        totalPages,
        currentPage,
        selectedInquiry,
        isLoadingHistory,
        // 상태 변경 UI 전용
        setCurrentPage,
        setSelectedInquiry,
        handleDelete,
        removeHistoryItem,
        addHistory,
        // 모든 필터 상태 및 선택자 노출
        personalSearch, setPersonalSearch,
        companyNameSearch, setCompanyNameSearch,
        companyContentSearch, setCompanyContentSearch,
        adminCompanySearch, setAdminCompanySearch,
        startDate, setStartDate,
        endDate, setEndDate,
        statusFilter, setStatusFilter,
        sortOrder, setSortOrder,
        clearDateFilter,
    };
};
